import { useEffect, useRef } from "react";

import { PLAYYARD_WATER_SURFACES } from "@/lib/constants";
import { PetYardWeatherCondition } from "@/types";

type RainCondition =
  | PetYardWeatherCondition.DRIZZLE
  | PetYardWeatherCondition.RAIN
  | PetYardWeatherCondition.THUNDERSTORM;

interface Drop {
  x: number;
  y: number;
  depth: number;
  landY: number;
}

interface BandStyle {
  strokeStyle: string;
  lineWidth: number;
}

interface Impact {
  x: number;
  y: number;
  ageMs: number;
  isRipple: boolean;
  size: number;
}

interface Bolt {
  points: [number, number][];
  branches: [number, number][][];
  ageMs: number;
}

const RAIN_SETTINGS: Record<
  RainCondition,
  { dropCount: number; speed: number; length: number; alpha: number }
> = {
  [PetYardWeatherCondition.DRIZZLE]: {
    dropCount: 140,
    speed: 0.75,
    length: 0.6,
    alpha: 0.7,
  },
  [PetYardWeatherCondition.RAIN]: {
    dropCount: 380,
    speed: 1,
    length: 1,
    alpha: 1,
  },
  [PetYardWeatherCondition.THUNDERSTORM]: {
    dropCount: 620,
    speed: 1.2,
    length: 1.25,
    alpha: 1.1,
  },
};
// Scene heights (0–1). Near drops land low on the lawn, far ones on the ridge.
const HORIZON = 0.56;
const GROUND_DEPTH = 0.42;
// Drops shallower than this are too far away to splash.
const SPLASH_MIN_DEPTH = 0.35;
const CALM_SLANT = 0.16;
const WINDY_SLANT = 0.42;
const SPLASH_MS = 220;
const RIPPLE_MS = 700;
const BOLT_MS = 260;
const MIN_BOLT_GAP_MS = 5_000;
const MAX_BOLT_GAP_MS = 13_000;
const MAX_FRAME_MS = 50;
// Night dims the rain in coarse steps so twilight doesn't restart the canvas
// every minute.
const DIM_STEPS = 4;
// Drops share one stroke per depth band instead of one per drop.
const DEPTH_BANDS = 4;
const WATER_SURFACES = [
  PLAYYARD_WATER_SURFACES.pond,
  ...PLAYYARD_WATER_SURFACES.puddles,
];

const randomBetween = (min: number, max: number) =>
  min + Math.random() * (max - min);

const isOnWater = (x: number, y: number, width: number, height: number) =>
  WATER_SURFACES.some(
    (surface) =>
      ((x / width - surface.centerX / 100) / (surface.radiusX / 100)) ** 2 +
        ((y / height - surface.centerY / 100) / (surface.radiusY / 100)) ** 2 <
      1,
  );

// Recycles a landed drop back to the top in place (no per-frame garbage).
// Depth stays inside the drop's band so it keeps drawing with that stroke.
const resetDrop = (
  drop: Drop,
  band: number,
  width: number,
  height: number,
  isFirst: boolean,
) => {
  drop.depth = (band + Math.random()) / DEPTH_BANDS;
  drop.x = randomBetween(-0.2, 1.05) * width;
  // The first batch starts mid-fall so the scene opens already raining.
  drop.y = isFirst
    ? randomBetween(-0.1, 1) * height
    : randomBetween(-0.3, 0) * height;
  drop.landY = (HORIZON + drop.depth * GROUND_DEPTH) * height;
};

// A jagged bolt from the top of the sky down to the ridge, with a fork or two.
const createBolt = (width: number, height: number): Bolt => {
  const points: [number, number][] = [];
  let x = randomBetween(0.15, 0.85) * width;
  const bottom = randomBetween(0.38, 0.5) * height;
  for (let y = 0; y < bottom; y += randomBetween(12, 26)) {
    points.push([x, y]);
    x += randomBetween(-18, 18);
  }
  const branches = points
    .filter((_, index) => index > 2 && Math.random() < 0.18)
    .map(([startX, startY]) => {
      const branch: [number, number][] = [[startX, startY]];
      let branchX = startX;
      const direction = Math.random() < 0.5 ? -1 : 1;
      for (let step = 1; step < 5; step += 1) {
        branchX += direction * randomBetween(6, 16);
        branch.push([branchX, startY + step * randomBetween(8, 14)]);
      }
      return branch;
    });
  return { points, branches, ageMs: 0 };
};

// Canvas rain: depth-layered drops, splashes on the grass, ripples on the
// pond and puddles, and lightning for storms. The simulation lives in the
// effect, not React state, so frames never re-render the tree.
export const PlayyardRain = ({
  condition,
  isWindy,
  nightAmount,
}: {
  condition: RainCondition;
  isWindy: boolean;
  nightAmount: number;
}) => {
  /* React Hooks */
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dimLevel = Math.round(nightAmount * DIM_STEPS) / DIM_STEPS;

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;

    const settings = RAIN_SETTINGS[condition];
    const slant = isWindy ? WINDY_SLANT : CALM_SLANT;
    const brightness = 1 - dimLevel * 0.35;
    const isStorm = condition === PetYardWeatherCondition.THUNDERSTORM;
    const isReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    let width = 0;
    let height = 0;
    let bands: Drop[][] = [];
    const impacts: Impact[] = [];
    let bolt: Bolt | null = null;
    let flash = 0;
    let nextBoltMs = randomBetween(MIN_BOLT_GAP_MS / 2, MAX_BOLT_GAP_MS);
    let frameId = 0;
    let lastTime = performance.now();

    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      bands = Array.from({ length: DEPTH_BANDS }, (_, band) =>
        Array.from({ length: settings.dropCount / DEPTH_BANDS }, () => {
          const drop = { x: 0, y: 0, depth: 0, landY: 0 };
          resetDrop(drop, band, width, height, true);
          return drop;
        }),
      );
      impacts.length = 0;
    };
    const bandStyles: BandStyle[] = Array.from(
      { length: DEPTH_BANDS },
      (_, band) => {
        const depth = (band + 0.5) / DEPTH_BANDS;
        const alpha = (0.12 + depth * 0.38) * settings.alpha * brightness;
        return {
          strokeStyle: `rgba(214, 226, 244, ${alpha.toFixed(3)})`,
          lineWidth: 0.6 + depth * 0.9,
        };
      },
    );

    const step = (elapsedMs: number) => {
      const fallPerMs = (height / 520) * settings.speed;
      bands.forEach((drops, band) => {
        drops.forEach((drop) => {
          const speed = fallPerMs * (0.55 + drop.depth * 0.9);
          drop.y += speed * elapsedMs;
          drop.x += speed * slant * elapsedMs;
          if (drop.y < drop.landY) return;

          if (drop.depth > SPLASH_MIN_DEPTH) {
            impacts.push({
              x: drop.x,
              y: drop.landY,
              ageMs: 0,
              isRipple: isOnWater(drop.x, drop.landY, width, height),
              size: 2 + drop.depth * 5,
            });
          }
          resetDrop(drop, band, width, height, false);
        });
      });
      // Age impacts and compact the finished ones out in place.
      let keptCount = 0;
      impacts.forEach((impact) => {
        impact.ageMs += elapsedMs;
        if (impact.ageMs < (impact.isRipple ? RIPPLE_MS : SPLASH_MS)) {
          impacts[keptCount] = impact;
          keptCount += 1;
        }
      });
      impacts.length = keptCount;

      if (!isStorm) return;
      nextBoltMs -= elapsedMs;
      if (nextBoltMs <= 0) {
        bolt = createBolt(width, height);
        flash = 1;
        nextBoltMs = randomBetween(MIN_BOLT_GAP_MS, MAX_BOLT_GAP_MS);
      }
      if (bolt) {
        bolt.ageMs += elapsedMs;
        if (bolt.ageMs > BOLT_MS) bolt = null;
      }
      flash = Math.max(0, flash - elapsedMs / 420);
    };

    const drawDrops = () => {
      context.lineCap = "round";
      bands.forEach((drops, band) => {
        context.strokeStyle = bandStyles[band].strokeStyle;
        context.lineWidth = bandStyles[band].lineWidth;
        context.beginPath();
        drops.forEach((drop) => {
          const length = (6 + drop.depth * 18) * settings.length;
          context.moveTo(drop.x, drop.y);
          context.lineTo(drop.x - length * slant, drop.y - length);
        });
        context.stroke();
      });
    };

    const drawImpacts = () => {
      impacts.forEach((impact) => {
        if (impact.isRipple) {
          const progress = impact.ageMs / RIPPLE_MS;
          context.strokeStyle = `rgba(235, 244, 255, ${(0.55 * (1 - progress) * brightness).toFixed(3)})`;
          context.lineWidth = 1;
          context.beginPath();
          context.ellipse(
            impact.x,
            impact.y,
            impact.size * (0.6 + progress * 2.4),
            impact.size * (0.2 + progress * 0.8),
            0,
            0,
            Math.PI * 2,
          );
          context.stroke();
          return;
        }
        // Grass splash: two droplets kicked up and out, fading as they drop.
        const progress = impact.ageMs / SPLASH_MS;
        const rise = Math.sin(progress * Math.PI) * impact.size;
        context.fillStyle = `rgba(225, 236, 250, ${(0.6 * (1 - progress) * brightness).toFixed(3)})`;
        [-1, 1].forEach((direction) => {
          context.beginPath();
          context.arc(
            impact.x + direction * progress * impact.size * 1.4,
            impact.y - rise,
            0.6 + impact.size * 0.12,
            0,
            Math.PI * 2,
          );
          context.fill();
        });
      });
    };

    const drawBolt = () => {
      if (flash > 0) {
        context.fillStyle = `rgba(232, 238, 255, ${(flash * 0.45).toFixed(3)})`;
        context.fillRect(0, 0, width, height);
      }
      if (!bolt) return;
      const alpha = 1 - bolt.ageMs / BOLT_MS;
      context.shadowColor = "rgba(200, 215, 255, 0.9)";
      context.shadowBlur = 14;
      context.strokeStyle = `rgba(250, 252, 255, ${alpha.toFixed(3)})`;
      [bolt.points, ...bolt.branches].forEach((line, index) => {
        context.lineWidth = index === 0 ? 2.4 : 1.2;
        context.beginPath();
        line.forEach(([x, y], pointIndex) => {
          if (pointIndex === 0) context.moveTo(x, y);
          else context.lineTo(x, y);
        });
        context.stroke();
      });
      context.shadowBlur = 0;
    };

    const draw = () => {
      context.clearRect(0, 0, width, height);
      drawDrops();
      drawImpacts();
      drawBolt();
    };

    const loop = (time: number) => {
      step(Math.min(time - lastTime, MAX_FRAME_MS));
      lastTime = time;
      draw();
      frameId = requestAnimationFrame(loop);
    };

    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    if (isReducedMotion) {
      draw();
    } else {
      frameId = requestAnimationFrame(loop);
    }

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
    };
  }, [condition, dimLevel, isWindy]);

  /* Render */
  return <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />;
};
